import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'shared/lib/request';
import { errorHandler, showMessage } from 'shared/feedback/messages';
import { i18n } from 'shared/i18n';
import { v4 as uuidv4 } from 'uuid';
import { styleKeys } from 'entities/style';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { EventType, dispatchMessage } from 'shared/feedback/toasts';
import { documentKeys } from './keys';
import { api } from 'shared/api/client';

// These document hooks are still on the raw request wrapper. Reasons, per hook:
//   - useGetDocuments, useGetDocumentBySuperId, useGetDocumentVersions: the
//     /documents GET returns an array and these do response-shape-dependent
//     post-processing (.length / .push / .selected).
//   - useCreateTemplateStyle: posts directly to /styles then dispatches an action.
//   - useExportDocument: binary (expectBinary) response — openapi-fetch can't model it.
// Everything else in this file is on the typed `api` client.
//   - useImportDocument (/documents/import/file) and useParagraphHistory
//     (/documents/:id/changes/:sectionId): endpoints not in the OpenAPI spec.

interface DocumentVersion {
  documentRevisionId: string;
  documentSuperId?: number | string;
  createdAt: string;
  draft?: boolean;
  name?: string;
  selected?: boolean;
}

export const useGetDocumentVersions = (
  documentId: number | string,
  documentRevisionId: string | undefined,
  draftDocument: { revisionId?: string; superId?: number | string; createdAt?: string | null } | undefined
) =>
  useQuery({
    queryKey: documentKeys.versions(documentId, draftDocument, documentRevisionId),

    queryFn: () => {
      return request
        .get(`/gaby/versions/${documentId}`)
        .then((res) => res.data)
        .then((versions: DocumentVersion[]) => {
          if (draftDocument && !versions.find((d) => d.documentRevisionId === draftDocument.revisionId)) {
            versions.push({
              documentSuperId: draftDocument.superId,
              documentRevisionId: draftDocument.revisionId ?? '',
              createdAt: draftDocument.createdAt ?? '',
              draft: true,
              name: 'Current draft',
            });
          }
          versions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          if (documentRevisionId) {
            const selected = versions.find((d) => d.documentRevisionId === documentRevisionId);
            if (selected) selected.selected = true;
          } else {
            versions[0].selected = true;
          }
          return versions;
        });
    },

    enabled: !!draftDocument,
  });

export const useGetDocuments = (selectedOrganization: unknown, showDeleted: boolean, orderBy: string, orderDirection: string) =>
  useQuery({
    queryKey: documentKeys.list({ selectedOrganization, showDeleted, orderBy, orderDirection }),

    queryFn: () => {
      return request
        .get(`/gaby/documents?current=true&organization=${selectedOrganization}&showDeleted=${showDeleted}&orderBy=${orderBy}&orderDirection=${orderDirection}`)
        .then((response) => response.data);
    },
  });

export const useGetDocumentBySuperId = (documentSuperId: number | string) => {
  return useQuery({
    queryKey: documentKeys.bySuperId(documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/documents/', { params: { query: { superId: Number(documentSuperId), current: 'true' } } });
      return data?.length ? data[0] : null;
    },

    refetchOnWindowFocus: false,
  });
};

export const useGetDocument = (documentId: number | string, selectedOrganization: unknown, revisionId?: string, useDraft = false, embedd = false) =>
  useQuery({
    queryKey: documentKeys.detail(documentId, selectedOrganization, revisionId),

    queryFn: async () => {
      const query = {
        organization: String(selectedOrganization),
        compileSass: 'true',
        useDraft: String(useDraft),
        embedd: String(embedd),
        showDeleted: 'true',
      };

      if (revisionId) {
        const { data } = await api.GET('/documents/{revisionId}', { params: { path: { revisionId }, query } });
        return data;
      }
      const { data } = await api.GET('/documents/', {
        params: { query: { ...query, superId: Number(documentId), current: 'true' } },
      });
      const latest = data[data.length - 1];
      if (!latest) throw new Error(`No current document for superId ${documentId}`);
      return latest;
    },

    enabled: !!(selectedOrganization && documentId),
  });

export const useCreateTemplateStyle = (documentId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ template }: { template: { style?: unknown; superId?: number | string; revisionId?: string } }) => {
      const newStyle = {
        id: uuidv4(),
        documentSuperId: documentId,
        content: template.style,
      };

      await request.post(`/gaby/styles`, newStyle);

      const response = await request.post(`/gaby/documents/${documentId}/actions?compileSass=true`, {
        action: 'create_template_style',
        revisionId: newStyle.id,
        data: {
          styleId: newStyle.id,
          templateSuperId: template.superId,
          templateRevisionId: template.revisionId,
        },
      });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });
};

export const useExportDocument = () =>
  useMutation({
    mutationFn: ({
      documentSuperId,
      documentRevisionId,
      selectedOrganization,
      documentName,
    }: {
      documentSuperId: number | string;
      documentRevisionId?: string;
      selectedOrganization?: unknown;
      documentName?: string;
    }) => {
      return request
        .post(
          `/gaby/documents/${documentSuperId}/actions`,
          {
            action: 'export_document',
            organization: selectedOrganization,
            revisionId: documentRevisionId,
            superId: documentSuperId,
          },
          undefined,
          undefined,
          { expectBinary: true }
        )
        .then((res) => {
          const blob = new Blob([res.data], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = res.data.name || `${documentName}.zip`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        });
    },

    onSuccess: () => {
      showMessage('success', i18n('project-details:document-export-success'));
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });

export const useImportDocument = ({ selectedOrganization }: { selectedOrganization: unknown }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentName }: { documentName: string }) => {
      return request.post(`/gaby/documents/import/file`, {
        name: documentName,
        organization: selectedOrganization,
      });
    },

    onSuccess: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('dashboard:document-import-success'),
        removeTime: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: documentKeys.list({ selectedOrganization }),
      });
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });
};

export const useParagraphHistory = (documentId: number | string, sectionId: string, revisionL: unknown, revisionR: unknown, nodeId: unknown) =>
  useQuery({
    queryKey: documentKeys.paragraphHistory(documentId, sectionId, revisionL, revisionR, nodeId),

    queryFn: async () => {
      const res = await api.GET('/documents/{documentSuperId}/changes/{sectionId}', {
        params: {
          path: { documentSuperId: Number(documentId), sectionId },
          query: { fromRevision: String(revisionL), toRevision: String(revisionR), identifier: String(nodeId) },
        },
      });
      return res.data;
    },

    enabled: !!(documentId && sectionId && revisionL && revisionR && nodeId),
  });

export const useApproveRevisions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (revisionIds: string[]) => api.POST('/revisions/', { body: revisionIds }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.changesAll],
      });
    },

    onError: ServerErrorHandler,
  });
};

export const useDocumentRevision = (revisionId: string | undefined, documentSuperId: number | string | undefined) =>
  useQuery({
    queryKey: documentKeys.revision(revisionId, documentSuperId),

    queryFn: async () => {
      if (revisionId) {
        const res = await request.get(`/gaby/documents/${revisionId}?compileSass=true`);
        return res.data;
      } else {
        const res = await request.get(`/gaby/documents?superId=${documentSuperId}&current=true&compileSass=true`);
        return res.data?.[0] || null;
      }
    },

    enabled: !!(revisionId || documentSuperId),
  });

export const useUpdateDocumentLocale = () =>
  useMutation({
    mutationFn: async ({
      documentId,
      revisionId,
      superId,
      locale,
    }: {
      documentId: number | string;
      revisionId?: string;
      superId?: number | string;
      locale: string;
    }) => {
      const res = await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) } },
        body: {
          action: 'update_document_locale',
          data: { revisionId, superId, locale },
        },
      });
      const docRes = await api.GET('/documents/{revisionId}', { params: { path: { revisionId: res.data.revisionId } } });
      return docRes.data;
    },

    onError: ServerErrorHandler,
  });
